import React, { useState, useEffect } from 'react';
import { FaCheck, FaTimes, FaCrown, FaStar, FaGem, FaPhone, FaVideo, FaEnvelope, FaHeart, FaSearch, FaChartLine, FaBolt, FaShieldAlt, FaHeadset, FaUserTie, FaInfinity, FaEye } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../services/api';

const Subscription = () => {
    const [plans, setPlans] = useState([]);
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [loading, setLoading] = useState(true);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const [usageStats, setUsageStats] = useState(null);

    // Message system
    const [messages, setMessages] = useState([]);

    const showMessage = (type, text) => {
        const id = Date.now();
        setMessages(prev => [...prev, { id, type, text }]);
        setTimeout(() => {
            setMessages(prev => prev.filter(m => m.id !== id));
        }, 5000);
    };

    useEffect(() => {
        fetchPlans();
        fetchCurrentSubscription();
        fetchUsageStats();
        verifyPaymentOnReturn(); // Check if user returned from payment
    }, []);

    const fetchPlans = async () => {
        try {
            const response = await api.get('/subscription/plans');
            setPlans(response.data);
        } catch (error) {
            console.error('Error fetching plans:', error);
            showMessage('error', 'Failed to load subscription plans.');
        } finally {
            setLoading(false);
        }
    };

    const fetchCurrentSubscription = async () => {
        try {
            const response = await api.get('/subscription/my-subscription');
            setCurrentSubscription(response.data);
        } catch (error) {
            console.error('Error fetching subscription:', error);
        }
    };

    const fetchUsageStats = async () => {
        try {
            const response = await api.get('/subscription/usage/stats');
            setUsageStats(response.data);
        } catch (error) {
            console.error('Error fetching usage stats:', error);
        }
    };

    // Verify payment when user returns from gateway
    const verifyPaymentOnReturn = async () => {
        const urlParams = new URLSearchParams(window.location.search);
        const paymentStatus = urlParams.get('payment');
        const txnId = urlParams.get('txnId');

        if (!paymentStatus) return;

        // Prevent double verification
        if (localStorage.getItem('paymentVerified') === txnId) return;

        setLoading(true);
        try {
            if (paymentStatus === 'success') {
                showMessage('success', 'Payment successful! Your premium plan is now active.');
                await fetchCurrentSubscription();
                await fetchUsageStats();

                if (txnId) localStorage.setItem('paymentVerified', txnId);
                localStorage.removeItem('pendingSubscriptionOrderId');
                localStorage.removeItem('pendingPlanName');
                localStorage.removeItem('pendingBillingCycle');

                // Clean URL
                window.history.replaceState({}, '', '/dashboard');
            } else {
                showMessage('error', 'Payment failed or was cancelled. Please try again.');
                window.history.replaceState({}, '', '/dashboard');
            }
        } catch (error) {
            console.error('Payment verification failed:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectPlan = (plan) => {
        if (plan.name === 'free') {
            showMessage('info', 'You are already on the free plan.');
            return;
        }
        setSelectedPlan(plan);
        setShowPaymentModal(true);
    };

    const loadWorldlineSDK = () => {
        return new Promise((resolve, reject) => {
            if (typeof window.$ !== 'undefined' && typeof window.$.pnCheckout !== 'undefined') {
                resolve(true);
                return;
            }

            const loadWorldline = () => {
                const script = document.createElement('script');
                script.src = 'https://www.paynimo.com/paynimocheckout/server/lib/checkout.js';
                script.onload = () => resolve(true);
                script.onerror = () => reject(new Error('Failed to load Worldline SDK'));
                document.body.appendChild(script);
            };

            // Include jQuery if missing since Worldline depends on it
            if (typeof window.jQuery === 'undefined') {
                const jqScript = document.createElement('script');
                jqScript.src = 'https://code.jquery.com/jquery-3.6.0.min.js';
                jqScript.onload = loadWorldline;
                jqScript.onerror = () => reject(new Error('Failed to load jQuery'));
                document.head.appendChild(jqScript);
            } else {
                loadWorldline();
            }
        });
    };

    const handleSubscribe = async () => {
        if (!selectedPlan) return;

        setPaymentProcessing(true);

        try {
            await loadWorldlineSDK();

            // 1. Generate Token
            const response = await api.post('/subscription/generate-token', {
                planName: selectedPlan.name,
                billingCycle
            });

            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to generate payment token');
            }

            const { token: paymentToken, merchantId, txnId, consumerId, amount } = response.data;

            // Save for verification callback
            localStorage.setItem('pendingSubscriptionOrderId', txnId);
            localStorage.setItem('pendingPlanName', selectedPlan.name);
            localStorage.setItem('pendingBillingCycle', billingCycle);

            setShowPaymentModal(false);

            // 2. Launch Worldline Checkout
            const reqJson = {
                features: {
                    enableAbortResponse: true,
                    enableExpressPay: false,
                    enableInstrumentDeRegistration: false,
                    enableMerTxnDetails: true,
                    showPGResponseMsg: true
                },
                consumerData: {
                    deviceId: 'WEBSH2', // SHA-512
                    token: paymentToken,
                    returnUrl: `${process.env.REACT_APP_API_URL}/subscription/callback`,
                    responseHandler: handleWorldlineResponse,
                    paymentMode: 'all',
                    merchantLogoUrl: 'https://www.paynimo.com/CompanyDocs/company-logo-vertical.png',
                    merchantId: merchantId,
                    currency: 'INR',
                    consumerId: consumerId,
                    consumerMobileNo: response.data.consumerMobileNo || '',
                    consumerEmailId: response.data.consumerEmailId || '',
                    txnId: txnId,
                    items: [{
                        itemId: 'first',
                        amount: amount.toString(),
                        comAmt: '0'
                    }],
                    cartDescription: 'Subscription Payment',
                    customStyle: {
                        PRIMARY_COLOR_CODE: '#e11d48', // rose-600
                        SECONDARY_COLOR_CODE: '#FFFFFF',
                        BUTTON_COLOR_CODE_1: '#e11d48',
                        BUTTON_COLOR_CODE_2: '#FFFFFF'
                    }
                }
            };

            window.$.pnCheckout(reqJson);

        } catch (error) {
            console.error('Payment initiation failed:', error);
            const errorMsg = error.response?.data?.message || error.message || 'Failed to start payment. Please try again.';
            showMessage('error', errorMsg);
        } finally {
            // setTimeout to prevent button flickering since popup handles it
            setTimeout(() => setPaymentProcessing(false), 2000);
        }
    };

    const handleWorldlineResponse = async (res) => {
        if (
            typeof res !== 'undefined' &&
            typeof res.paymentMethod !== 'undefined' &&
            typeof res.paymentMethod.paymentTransaction !== 'undefined' &&
            typeof res.paymentMethod.paymentTransaction.statusCode !== 'undefined'
        ) {
            const statusCode = res.paymentMethod.paymentTransaction.statusCode;

            if (statusCode === '0300') {
                // Success
                showMessage('info', 'Payment successful! Verifying with server...');
                try {
                    const planName = localStorage.getItem('pendingPlanName');
                    const billingCycle = localStorage.getItem('pendingBillingCycle');
                    
                    const verifyRes = await api.post('/subscription/verify-payment', {
                        responseData: res.stringResponse || res, 
                        planName,
                        billingCycle
                    });
                    
                    if (verifyRes.data.success) {
                        showMessage('success', 'Subscription activated!');
                        await fetchCurrentSubscription();
                        await fetchUsageStats();
                        localStorage.removeItem('pendingSubscriptionOrderId');
                    } else {
                        showMessage('error', 'Payment verification failed.');
                    }
                } catch (err) {
                    console.error(err);
                    showMessage('error', 'Error verifying payment.');
                }
            } else if (statusCode === '0392') {
                showMessage('error', 'Payment was cancelled');
            } else {
                showMessage('error', 'Payment failed. Please try again.');
            }
        }
    };


    const calculatePrice = (plan) => {
        if (billingCycle === 'monthly') return plan.price.monthly;
        if (billingCycle === 'quarterly') return plan.price.quarterly;
        return plan.price.yearly;
    };

    const calculateSavings = (plan) => {
        const monthly = plan.price.monthly;
        if (billingCycle === 'quarterly') {
            const fullPrice = monthly * 3;
            const savings = fullPrice - plan.price.quarterly;
            return savings > 0 ? `Save ₹${savings}` : null;
        }
        if (billingCycle === 'yearly') {
            const fullPrice = monthly * 12;
            const savings = fullPrice - plan.price.yearly;
            return savings > 0 ? `Save ₹${savings}` : null;
        }
        return null;
    };

    const getFeatureList = (features) => {
        const featureList = [];

        if (features.profileViews === -1) {
            featureList.push({ text: 'Unlimited Profile Views', icon: <FaInfinity />, highlight: true });
        } else if (features.profileViews > 0) {
            featureList.push({ text: `${features.profileViews} Profile Views`, icon: <FaEye /> });
        }

        if (features.canSendMessages) {
            featureList.push({ text: 'Unlimited Messaging', icon: <FaEnvelope />, highlight: true });
        }

        if (features.contactViewQuota === -1) {
            featureList.push({ text: 'Unlimited Contact Reveals', icon: <FaPhone />, highlight: true });
        } else if (features.contactViewQuota > 0) {
            featureList.push({ text: `${features.contactViewQuota} Contact Reveals`, icon: <FaPhone /> });
        }

        if (features.canSendFiles) {
            featureList.push({ text: 'File & Photo Sharing', icon: <FaGem /> });
        }

        if (features.canSendVoiceMessages) {
            featureList.push({ text: 'Voice Messages', icon: <FaPhone /> });
        }

        if (features.canReactToMessages) {
            featureList.push({ text: 'Message Reactions', icon: <FaHeart /> });
        }

        if (features.voiceCalls) {
            featureList.push({ text: 'Voice Calls', icon: <FaPhone />, highlight: true });
        }

        if (features.videoCalls) {
            featureList.push({ text: 'Video Calls', icon: <FaVideo />, highlight: true });
        }

        if (features.canSeeWhoViewedProfile) {
            featureList.push({ text: 'See Who Viewed You', icon: <FaEye /> });
        }

        if (features.canSeeWhoShortlisted) {
            featureList.push({ text: 'See Who Shortlisted You', icon: <FaHeart /> });
        }

        if (features.advancedSearch) {
            featureList.push({ text: 'Advanced Search Filters', icon: <FaSearch /> });
        }

        if (features.priorityListing) {
            featureList.push({ text: 'Priority Profile Listing', icon: <FaBolt />, highlight: true });
        }

        if (features.highlightedProfile) {
            featureList.push({ text: 'Highlighted Profile', icon: <FaStar />, highlight: true });
        }

        if (features.verifiedBadge) {
            featureList.push({ text: 'Verified Badge', icon: <FaShieldAlt /> });
        }

        if (features.hideAds) {
            featureList.push({ text: 'Ad-Free Experience', icon: <FaCheck /> });
        }

        if (features.prioritySupport) {
            featureList.push({ text: 'Priority Support', icon: <FaHeadset />, highlight: true });
        }

        if (features.dedicatedManager) {
            featureList.push({ text: 'Dedicated Relationship Manager', icon: <FaUserTie />, highlight: true });
        }

        if (features.horoscopeMatching) {
            featureList.push({ text: 'Horoscope Matching', icon: <FaStar /> });
        }

        if (features.jathagam) {
            featureList.push({ text: 'Jathagam Analysis', icon: <FaChartLine /> });
        }

        if (features.marketAccess) {
            featureList.push({ text: 'Market Access', icon: <FaGem /> });
        }

        return featureList;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600 mx-auto"></div>
                    <p className="mt-4 text-text-secondary">Loading plans...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full mx-auto px-4 py-8 relative">
            {/* Inline Messages */}
            <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-3">
                <AnimatePresence>
                    {messages.map(msg => (
                        <motion.div
                            key={msg.id}
                            initial={{ opacity: 0, y: -50 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -50 }}
                            className={`px-6 py-3 rounded-full shadow-lg text-white font-medium text-sm flex items-center gap-3 ${msg.type === 'success' ? 'bg-green-600' :
                                msg.type === 'error' ? 'bg-red-600' :
                                    'bg-blue-600'
                                }`}
                        >
                            {msg.type === 'success' && <FaCheck />}
                            {msg.type === 'error' && <FaTimes />}
                            {msg.type === 'info' && <FaCrown />}
                            {msg.text}
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>

            {/* Header */}
            <div className="text-center mb-12">
                <motion.h1
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-orange-500 bg-clip-text text-transparent mb-4"
                >
                    Choose Your Perfect Plan
                </motion.h1>
                <p className="text-text-secondary text-lg">
                    Find your life partner with our premium features
                </p>
            </div>

            {/* Current Plan Status */}
            {currentSubscription && currentSubscription.currentPlan !== 'free' && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8 p-6 bg-gradient-to-r from-rose-50 to-pink-50 rounded-2xl border-2 border-primary-pink/50 shadow-[0_0_15px_rgba(255,46,136,0.2)]"
                >
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                            <div>
                                <h3 className="text-xl font-bold text-text-primary">
                                    Current Plan: {currentSubscription.planDetails?.displayName}
                                </h3>
                                <p className="text-sm text-text-secondary">
                                    {currentSubscription.isActive ? (
                                        <>
                                            Active until {new Date(currentSubscription.expiryDate).toLocaleDateString()}
                                        </>
                                    ) : (
                                        <span className="text-red-600">Expired</span>
                                    )}
                                </p>
                            </div>
                        </div>
                        {usageStats && (
                            <div className="flex gap-4">
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-primary-pink">
                                        {usageStats.usageStats.profileViewsToday}
                                    </p>
                                    <p className="text-xs text-text-secondary">Views Today</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-pink-600">
                                        {usageStats.usageStats.messagesSentToday}
                                    </p>
                                    <p className="text-xs text-text-secondary">Messages Today</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-orange-600">
                                        {usageStats.usageStats.contactsRevealedTotal}
                                    </p>
                                    <p className="text-xs text-text-secondary">Contacts Revealed</p>
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>
            )}

            {/* Billing Cycle Toggle */}
            <div className="flex justify-center mb-12">
                <div className="bg-[rgba(255,255,255,0.05)] p-1.5 rounded-lg inline-flex border border-glass-border">
                    <button
                        onClick={() => setBillingCycle('monthly')}
                        className={`px-6 py-2 rounded-md text-sm font-semibold transition-all ${billingCycle === 'monthly'
                            ? 'bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass text-text-primary shadow-sm ring-1 ring-black/5'
                            : 'text-text-muted hover:text-text-primary drop-shadow-sm'
                            }`}
                    >
                        Monthly
                    </button>
                    <button
                        onClick={() => setBillingCycle('quarterly')}
                        className={`px-6 py-2 rounded-md text-sm font-semibold transition-all relative ${billingCycle === 'quarterly'
                            ? 'bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass text-text-primary shadow-sm ring-1 ring-black/5'
                            : 'text-text-muted hover:text-text-primary drop-shadow-sm'
                            }`}
                    >
                        Quarterly
                        <span className="absolute -top-2.5 -right-1 bg-green-100 text-green-700 text-[10px] font-bold px-1.5 py-0.5 rounded border border-green-200">
                            -10%
                        </span>
                    </button>
                    <button
                        onClick={() => setBillingCycle('yearly')}
                        className={`px-6 py-2 rounded-md text-sm font-semibold transition-all relative ${billingCycle === 'yearly'
                            ? 'bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass text-text-primary shadow-sm ring-1 ring-black/5'
                            : 'text-text-muted hover:text-text-primary drop-shadow-sm'
                            }`}
                    >
                        Yearly
                        <span className="absolute -top-2.5 -right-1 bg-orange-100 text-orange-700 text-[10px] font-bold px-1.5 py-0.5 rounded border border-orange-200">
                            -20%
                        </span>
                    </button>
                </div>
            </div>

            {/* Plans Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                {plans.map((plan, index) => {
                    const isCurrentPlan = currentSubscription?.currentPlan === plan.name;
                    const featureList = getFeatureList(plan.features);
                    const price = calculatePrice(plan);
                    const savings = calculateSavings(plan);

                    return (
                        <motion.div
                            key={plan._id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className={`relative rounded-3xl p-8 ${plan.popular
                                ? 'bg-gradient-to-br from-rose-600 to-pink-600 text-white shadow-2xl scale-105 border-4 border-yellow-400'
                                : 'bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass border-2 border-glass-border hover:border-rose-300 hover:shadow-xl'
                                } transition-all duration-300`}
                        >
                            {plan.popular && (
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-yellow-400 text-rose-900 px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                                    ⭐ MOST POPULAR
                                </div>
                            )}

                            <div className="text-center mb-6">
                                <h3 className={`text-2xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-text-primary'}`}>
                                    {plan.displayName}
                                </h3>
                                <div className="flex items-baseline justify-center gap-2">
                                    <span className={`text-4xl font-bold ${plan.popular ? 'text-white' : 'text-primary-pink'}`}>
                                        ₹{price}
                                    </span>
                                    <span className={`text-sm ${plan.popular ? 'text-rose-100' : 'text-text-secondary'}`}>
                                        /{billingCycle === 'yearly' ? 'year' : billingCycle === 'quarterly' ? 'quarter' : 'month'}
                                    </span>
                                </div>
                                {savings && (
                                    <p className={`text-sm mt-2 font-medium ${plan.popular ? 'text-yellow-300' : 'text-green-600'}`}>
                                        {savings}
                                    </p>
                                )}
                            </div>

                            <ul className="space-y-3 mb-8">
                                {featureList.slice(0, 10).map((feature, idx) => (
                                    <li key={idx} className="flex items-start gap-2">
                                        <span className={`mt-1 ${feature.highlight ? 'text-yellow-400' : plan.popular ? 'text-rose-200' : 'text-primary-pink'}`}>
                                            {feature.icon || <FaCheck />}
                                        </span>
                                        <span className={`text-sm ${plan.popular ? 'text-white' : 'text-text-secondary'} ${feature.highlight ? 'font-bold' : ''}`}>
                                            {feature.text}
                                        </span>
                                    </li>
                                ))}
                                {featureList.length > 10 && (
                                    <li className={`text-sm italic ${plan.popular ? 'text-rose-200' : 'text-text-secondary'}`}>
                                        +{featureList.length - 10} more features
                                    </li>
                                )}
                            </ul>

                            <button
                                onClick={() => handleSelectPlan(plan)}
                                disabled={isCurrentPlan && currentSubscription?.isActive}
                                className={`w-full py-3 rounded-xl font-bold transition-all ${isCurrentPlan && currentSubscription?.isActive
                                    ? 'bg-gray-300 text-text-secondary cursor-not-allowed'
                                    : plan.popular
                                        ? 'bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass text-primary-pink hover:bg-yellow-400 hover:text-rose-900 shadow-lg hover:shadow-xl'
                                        : plan.name === 'free'
                                            ? 'bg-[rgba(255,255,255,0.08)] text-text-secondary hover:bg-gray-300'
                                            : 'bg-gradient-to-r from-rose-600 to-pink-600 text-white hover:shadow-lg hover:scale-105'
                                    }`}
                            >
                                {isCurrentPlan && currentSubscription?.isActive ? (
                                    <>
                                        <FaCheck className="inline mr-2" />
                                        Current Plan
                                    </>
                                ) : plan.name === 'free' ? (
                                    'Free Forever'
                                ) : (
                                    <>
                                        <FaCrown className="inline mr-2" />
                                        Upgrade Now
                                    </>
                                )}
                            </button>
                        </motion.div>
                    );
                })}
            </div>

            {/* Feature Comparison Table */}
            <div className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-3xl shadow-xl p-8 mb-12">
                <h2 className="text-3xl font-bold text-center mb-8 bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
                    Detailed Feature Comparison
                </h2>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b-2 border-glass-border">
                                <th className="text-left p-4 font-bold text-text-primary">Feature</th>
                                {plans.map(plan => (
                                    <th key={plan._id} className="p-4 text-center">
                                        <div className="font-bold text-text-primary">{plan.displayName}</div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <FeatureRow label="Profile Views" plans={plans} feature="profileViews" />
                            <FeatureRow label="Contact Reveals" plans={plans} feature="contactViewQuota" />
                            <FeatureRow label="Messaging" plans={plans} feature="canSendMessages" />
                            <FeatureRow label="File Sharing" plans={plans} feature="canSendFiles" />
                            <FeatureRow label="Voice Calls" plans={plans} feature="voiceCalls" />
                            <FeatureRow label="Video Calls" plans={plans} feature="videoCalls" />
                            <FeatureRow label="See Who Viewed" plans={plans} feature="canSeeWhoViewedProfile" />
                            <FeatureRow label="Priority Listing" plans={plans} feature="priorityListing" />
                            <FeatureRow label="Verified Badge" plans={plans} feature="verifiedBadge" />
                            <FeatureRow label="Ad-Free" plans={plans} feature="hideAds" />
                            <FeatureRow label="Priority Support" plans={plans} feature="prioritySupport" />
                            <FeatureRow label="Dedicated Manager" plans={plans} feature="dedicatedManager" />
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Payment Modal */}
            <AnimatePresence>
                {showPaymentModal && selectedPlan && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                        onClick={() => !paymentProcessing && setShowPaymentModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-3xl p-8 max-w-md w-full"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="text-center mb-6">
                                <h3 className="text-2xl font-bold text-text-primary mb-2">
                                    Subscribe to {selectedPlan.displayName}
                                </h3>
                                <div className="flex items-baseline justify-center gap-2">
                                    <span className="text-4xl font-bold text-primary-pink">
                                        ₹{calculatePrice(selectedPlan)}
                                    </span>
                                    <span className="text-text-secondary">
                                        /{billingCycle === 'yearly' ? 'year' : billingCycle === 'quarterly' ? 'quarter' : 'month'}
                                    </span>
                                </div>
                                {calculateSavings(selectedPlan) && (
                                    <p className="text-green-600 font-medium mt-2">
                                        {calculateSavings(selectedPlan)}
                                    </p>
                                )}
                            </div>

                            <div className="bg-[rgba(255,255,255,0.03)] rounded-2xl p-6 mb-6">
                                <h4 className="font-bold text-text-primary mb-4">What you'll get:</h4>
                                <ul className="space-y-2">
                                    {getFeatureList(selectedPlan.features).slice(0, 5).map((feature, idx) => (
                                        <li key={idx} className="flex items-center gap-2 text-sm text-text-secondary">
                                            <FaCheck className="text-green-600" />
                                            {feature.text}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowPaymentModal(false)}
                                    disabled={paymentProcessing}
                                    className="flex-1 px-6 py-3 border-2 border-glass-border text-text-secondary font-bold rounded-xl hover:bg-[rgba(255,255,255,0.03)] transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubscribe}
                                    disabled={paymentProcessing}
                                    className="flex-1 px-6 py-3 bg-gradient-to-r from-rose-600 to-pink-600 text-white font-bold rounded-xl hover:shadow-lg transition-all disabled:opacity-50"
                                >
                                    {paymentProcessing ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white inline-block mr-2"></div>
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <FaCrown className="inline mr-2" />
                                            Proceed to Payment
                                        </>
                                    )}
                                </button>
                            </div>

                            <p className="text-xs text-text-muted text-center mt-4">
                                By subscribing, you agree to our Terms of Service and Privacy Policy
                            </p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const FeatureRow = ({ label, plans, feature }) => {
    const getValue = (plan) => {
        const value = plan.features[feature];
        if (typeof value === 'boolean') {
            return value ? <FaCheck className="text-green-600 mx-auto" /> : <FaTimes className="text-red-400 mx-auto" />;
        }
        if (value === -1) {
            return <span className="text-primary-pink font-bold">Unlimited</span>;
        }
        if (value === 0) {
            return <FaTimes className="text-red-400 mx-auto" />;
        }
        return <span className="text-text-primary font-medium">{value}</span>;
    };

    return (
        <tr className="border-b border-glass-border hover:bg-[rgba(255,255,255,0.03)]">
            <td className="p-4 font-medium text-text-secondary">{label}</td>
            {plans.map(plan => (
                <td key={plan._id} className="p-4 text-center">
                    {getValue(plan)}
                </td>
            ))}
        </tr>
    );
};

export default Subscription;